import queryString from 'querystring'

import { NextPage } from 'next'
import { withApollo } from 'components/withApollo'
import PageLayout from 'components/pages/PageLayout'
import { useEffect, useMemo, useState } from 'react'
import parseISO from 'date-fns/parseISO'
import getMutationErrors, {
  getMutationErrorData
} from 'utils/getMutationErrors'
import { useRouter } from 'next/router'
import toApiDateTime from 'utils/toApiDateTime'
import PageMeta from 'components/PageMeta'
import Link from 'next/link'
import { InformationCircleIcon } from '@heroicons/react/24/outline'
import BackButton from 'components/ui/BackButton'
import { cn } from 'cn'
import { logEvent, setAdsUserData } from 'utils/analytics'
import { useCateringRequestFormCategories } from 'components/pages/CateringRequestFormPage/types/CateringRequestFormCategories'
import sortBy from 'lodash/sortBy'
import { OptionGroup } from 'components/ui/DeepRadioGroup'
import pickBy from 'lodash/pickBy'
import identity from 'lodash/identity'
import {
  clearSavedFormValues,
  getSavedFormValues
} from 'components/FormValuesRecorder/helpers'
import ChatWidget from 'components/ChatWidget'
import LoadingPage from 'components/pages/LoadingPage'
import { useCateringRequestPreferredTruck } from 'components/pages/CateringRequestFormPage/types/CateringRequestPreferredTruck'

import { useTheme, withOrganizationTheme } from 'lib/theme'
import { HireOptionKey, WizardStep } from 'lib/theme/themes'

import * as Types from 'global-graphql-types'
import useInfoDialog from 'hooks/useInfoDialog'
import { User } from 'queries/useUser'

import CateringRequestStepsForm, {
  FormValues
} from './CateringRequestStepsForm'
import { useLatestCustomerCateringRequest } from './types/LatestCustomerCateringRequest'
import { useCateringRequestCreate } from './types/CateringRequestCreate'
import { useCateringFormOrganizationQuery } from './types/CateringFormOrganizationQuery'

function CateringRequestFormPage() {
  const { theme, isOrganizer } = useTheme()
  const [isClient, setIsClient] = useState(false)

  useEffect(() => {
    setIsClient(true)
  }, [])
  const [cateringRequestCreate] = useCateringRequestCreate({})
  const infoDialog = useInfoDialog()
  const router = useRouter()
  const [organizationId, setOrganizationId] = useState<number | null>(null)
  const {
    organizer: organizerSeoName,
    truckId: preferredTruckId,
    event: initialCateringType,
    note: urlNote,
    copyFrom,
    copyFromJson,
    hideBackButton
  } = (router.query || {}) as {
    organizer?: string
    truckId?: string
    event?: string
    note?: string
    copyFrom?: string
    copyFromJson?: string
    hideBackButton?: 'true'
  }

  useEffect(() => {
    if (urlNote) {
      sessionStorage.setItem('cateringNote', urlNote)
    }
  }, [urlNote])

  const { data: truckData } = useCateringRequestPreferredTruck({
    variables: {
      id: preferredTruckId ? parseInt(preferredTruckId, 10) : null
    },
    skip: !preferredTruckId,
    fetchPolicy: 'cache-first'
  })

  const { data: categoriesData } = useCateringRequestFormCategories({
    fetchPolicy: 'cache-first'
  })

  const { data: latestRequestData } = useLatestCustomerCateringRequest({
    skip: typeof window === 'undefined',
    variables: {
      id: copyFrom ? parseInt(copyFrom, 10) : null
    }
  })

  const initialValues: Partial<FormValues> = useMemo(() => {
    if (copyFromJson) {
      try {
        const parsed: Partial<FormValues> = JSON.parse(copyFromJson)
        if (parsed.startAt) {
          parsed.startAt = new Date(parsed.startAt)
          parsed.date = new Date(parsed.startAt)
        } else {
          delete parsed.date
        }
        if (parsed.endAt) {
          parsed.endAt = new Date(parsed.endAt)
        }
        return {
          ...parsed
        } as Partial<FormValues>
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e)
      }
    }
    const savedValues = getSavedFormValues<FormValues>('CATERING_FORM_VALUES', {
      date: parseISO,
      startAt: parseISO,
      endAt: parseISO
    })

    if (savedValues && Object.keys(savedValues).length > 0) {
      return savedValues
    }

    const lastRequest = latestRequestData?.cateringRequests?.records?.[0]
    if (!lastRequest) {
      return {
        preferredContactMethod:
          Types.CateringPreferredContactMethod.ContactByPhoneOrEmail
      }
    }

    return {
      attendees: lastRequest.attendees.toString(),
      cateringType: initialCateringType ?? lastRequest.cateringType,
      address: lastRequest.fullAddress,
      additionalInstructions: lastRequest.additionalInstructions,
      foodPreferences: lastRequest.foodPreferences,
      customerTotalBudget: lastRequest.customerTotalBudget.cents,
      preferredContactMethod:
        lastRequest.preferredContactMethod as unknown as Types.CateringPreferredContactMethod,
      mealParts: lastRequest.mealParts.map((part) => ({
        id: part.id,
        name: part.name,
        count: part.count
      }))
    } as Partial<FormValues>
  }, [latestRequestData, initialCateringType, copyFromJson])

  const categoryOptions: OptionGroup<string>[] = useMemo(() => {
    if (!categoriesData?.cateringCategories) return []

    return sortBy(
      categoriesData.cateringCategories.filter(
        (c) => !c.mainPage && !c.parentCategory
      ),
      'name'
    ).map((c): OptionGroup<string> => {
      const options = [
        ...sortBy(c.childCategories, 'name').map((child) => ({
          label: child.name,
          value: child.name
        }))
      ]
      if (c.childCategories.length > 0) {
        options.push({
          label: `${c.name} - Other`,
          value: `${c.name} - Other`
        })
      }
      return {
        label: c.name,
        value: c.name,
        options
      }
    })
  }, [categoriesData])

  const onSubmit = async (values: FormValues, user: User) => {
    const variables = {
      ...values,
      additionalInstructions: values.additionalInstructions ?? null,
      foodPreferences: values.foodPreferences ?? null,
      cateringType: values.cateringType,
      startAt: toApiDateTime(values.startAt),
      endAt: toApiDateTime(values.endAt),
      attendees: parseInt(values.attendees, 10),
      preferredTruckId: preferredTruckId
        ? parseInt(preferredTruckId, 10)
        : null,
      copyFrom: copyFrom ? parseInt(copyFrom, 10) : null,
      organizationId,
      creationNote: sessionStorage.getItem('cateringNote'),
      draft: false,
      fixedLocation: false
    }

    const response = await cateringRequestCreate({
      variables
    })
    if (!response) return undefined

    const errors = getMutationErrors(response, {
      ...values,
      requestInProgress: true,
      requestTooFarInAdvance: true,
      recommendDropOff: true
    })
    if (errors) {
      if (errors.requestInProgress) {
        const errorData = getMutationErrorData<{ catering_request_id: number }>(
          response,
          'requestInProgress'
        )
        errors.requestInProgress = (
          <>
            You already have an catering request in progress. If you want to
            submit another request please cancel{' '}
            <Link
              className="underline"
              href={`/catering/requests/${errorData!.catering_request_id}`}
            >
              the existing request
            </Link>{' '}
            or contact support{' '}
            {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
            <a className="underline" href="sms:+18333780040">
              (833) 378-0040
            </a>
          </>
        )
      }
      if (errors.requestTooFarInAdvance) {
        errors.requestTooFarInAdvance = (
          <>
            Please contact the BFT catering team at{' '}
            {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
            <a className="underline" href="mailto:catering@bestfoodtrucks.com">
              catering@bestfoodtrucks.com
            </a>{' '}
            if you&apos;d like to discuss events that will be occurring more
            than 12 months from today.
          </>
        )
      }
      if (errors.recommendDropOff) {
        errors.recommendDropOff = (
          <>
            {errors.recommendDropOff}
            <br />
            <br />
            <Link
              className="underline font-medium"
              href={`/catering-drop-off/form?copyFromJson=${encodeURIComponent(
                JSON.stringify({
                  ...values,
                  startAt: values.startAt?.toISOString(),
                  endAt: values.endAt?.toISOString()
                })
              )}`}
            >
              Submit a drop off request
            </Link>
          </>
        )
      }
      return errors
    }

    if (variables.draft) {
      logEvent('generate_lead', {
        value: variables.customerTotalBudget / 100,
        currency: 'USD',
        form_name: 'catering_request'
      })

      await router.push('/hire-food-truck')

      infoDialog({
        title: 'Draft Saved',
        content: (
          <div className="prose antialiased mt-6">
            <p>
              The draft of this catering request was sent to your email address.
              Whenever you are ready to continue, you can click the link in the
              email to resume the request.
            </p>
            <p>
              <strong>-The BFT Team</strong>
            </p>
          </div>
        )
      })
    } else {
      const cateringRequest =
        response.data!.cateringRequestCreate!.cateringRequest!

      await router.push({
        pathname: `/catering/requests/${cateringRequest.code}`,
        query: preferredTruckId
          ? {
              truckId: preferredTruckId
            }
          : {}
      })

      await setAdsUserData(user)

      logEvent('purchase', {
        transaction_id: cateringRequest.id,
        currency: 'USD',
        value: cateringRequest.customerTotalBudget.cents / 100,
        purchase_type: 'catering_request',
        items: [
          {
            item_id: 'catering_request',
            item_name: 'Catering Request'
          }
        ]
      })

      // catering_request_submission
      logEvent('conversion', {
        transaction_id: cateringRequest.id,
        currency: 'USD',
        value: cateringRequest.customerTotalBudget.cents / 100,
        send_to: 'AW-768772864/qu_7CPzVnokZEICWyu4C',
        items: [
          {
            item_id: 'catering_request',
            item_name: 'Catering Request'
          }
        ]
      })

      clearSavedFormValues('CATERING_FORM_VALUES')
    }

    return undefined
  }

  const { data: organizationData } = useCateringFormOrganizationQuery({
    variables: {
      name: organizerSeoName!
    },
    fetchPolicy: 'cache-first',
    skip: !organizerSeoName || !organizerSeoName.length
  })

  useEffect(() => {
    if (organizationData?.organization) {
      setOrganizationId(organizationData?.organization.id)
    }
  }, [organizationData])

  const meta = (
    <PageMeta
      canonicalPath="/catering/form"
      noIndex={!!organizerSeoName}
      title="Best Food Trucks | Hire a food truck"
    />
  )

  if (!isClient) {
    return <LoadingPage hideNavigation={!!organizerSeoName} meta={meta} />
  }

  // If the organizer has only one host-serving option (catering or dropOff),
  // the ServingMethod step was skipped, so "back" should go to PaymentType
  const hostServingKeys: HireOptionKey[] = ['catering', 'dropOff']
  const backStep =
    isOrganizer &&
    hostServingKeys.filter((key) => theme.visibleHireOptions.includes(key))
      .length <= 1
      ? WizardStep.PaymentType
      : WizardStep.ServingMethod

  const serializedQuery = queryString.stringify(
    pickBy(
      {
        truckId: preferredTruckId,
        event: initialCateringType,
        step: backStep
      },
      identity
    )
  )

  const logoUrl =
    theme.logoUrl || organizationData?.organization?.cateringLogo?.variantUrl

  const themedBackground = isOrganizer && theme.page.themedBackground

  const backButtonHref = organizerSeoName
    ? `/catering/by/${organizerSeoName}/form?${serializedQuery}`
    : `/hire-food-truck?${serializedQuery}`

  const headerAction = !hideBackButton ? (
    <BackButton href={backButtonHref} />
  ) : undefined

  return (
    <div className={cn({ 'bg-theme-primary': theme.page.themedBackground })}>
      <ChatWidget />
      <PageLayout
        headerAction={headerAction}
        hideNavigation={!!organizerSeoName}
        meta={meta}
        gray={theme.page.grayBackground}
        hideFooter={theme.page.hideFooter}
        fullWidth={theme.page.fullWidth}
      >
        <div
          className={cn('min-h-screen w-full', {
            'bg-theme-primary font-theme-body': theme.page.themedBackground
          })}
        >
          {themedBackground ? (
            <>
              <div className="flex justify-center w-full pt-6 md:pt-10 mb-12">
                <div className="flex flex-col items-center gap-2">
                  {logoUrl ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      alt={`${theme.name} logo`}
                      className="h-10 object-contain"
                      src={logoUrl}
                    />
                  ) : (
                    <span className="text-3xl font-bold text-white">
                      {theme.name}
                    </span>
                  )}
                  <span className="text-xs tracking-widest uppercase text-white/60">
                    Powered by{' '}
                    <span className="text-theme-accent font-semibold">
                      Best Food Trucks
                    </span>
                  </span>
                </div>
              </div>
              <div className="text-center mb-12 px-4">
                <h1 className="text-4xl md:text-5xl font-theme-heading font-bold text-white mb-4">
                  Catering Request
                </h1>
                <p className="text-white/70 text-lg max-w-2xl mx-auto">
                  A great option when the host pays for everyone. Ideal for
                  company catering, meetings, or private events.
                </p>
              </div>
            </>
          ) : (
            <>
              {!hideBackButton && !organizerSeoName && (
                <div className="absolute top-20 left-0 right-0 z-10">
                  <div className="max-w-7xl mx-auto lg:mt-16 xl:mt-10 px-4">
                    <BackButton href={backButtonHref} />
                  </div>
                </div>
              )}
              <div className="mt-16 xl:mt-10">
                {organizationData &&
                  organizationData.organization?.cateringLogo && (
                    <div className="flex items-center justify-center mt-10 mb-6">
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img
                        alt={`${organizationData.organization.name} logo`}
                        className="bg-center object-cover"
                        src={
                          organizationData.organization?.cateringLogo.variantUrl
                        }
                      />
                    </div>
                  )}
                <h1 className="font-theme-heading font-bold text-theme-primary sm:text-5xl text-4xl text-center">
                  Catering Request
                </h1>
                <div className="text-gray-700 text-center mt-4 mb-6">
                  A great option when the host pays for everyone. <br /> Ideal
                  for company catering, meetings, or private events.
                </div>
                {truckData?.truck && (
                  <div className="text-center mb-8 border rounded-full border-theme-primary-border bg-theme-primary-light py-2 px-4 max-w-max mx-auto">
                    <span className="font-light text-center mb-8">
                      My preferred truck is{' '}
                    </span>
                    <span className="text-xl font-bold font-theme-heading text-theme-primary">
                      {truckData.truck.name}
                    </span>
                  </div>
                )}
              </div>
            </>
          )}
          <div className="flex flex-col max-w-screen-sm w-full pb-16 mx-auto">
            <div className="m-4 md:m-0">
              <CateringRequestStepsForm
                categoryOptions={categoryOptions}
                initialCateringType={initialCateringType}
                initialValues={initialValues}
                onSubmit={onSubmit}
              />
              {themedBackground ? (
                <div className="text-center mt-16 pb-12 text-sm text-white/60 px-4">
                  <p>
                    This catering is organized by Best Food Trucks on behalf of{' '}
                    {theme.name}.
                  </p>
                  <p className="mt-1">
                    Questions?{' '}
                    {/* eslint-disable-next-line nextjs/no-html-link-for-pages */}
                    <a
                      className="text-theme-accent hover:underline"
                      href={`mailto:${
                        organizationData?.organization?.cateringEmailCustomer ||
                        'sales@bestfoodtrucks.com'
                      }`}
                    >
                      {organizationData?.organization?.cateringEmailCustomer ||
                        'sales@bestfoodtrucks.com'}
                    </a>
                  </p>
                </div>
              ) : (
                organizationData && (
                  <div className="flex flex-row my-10 max-w-xl mx-auto">
                    <div>
                      <InformationCircleIcon className="text-gray-800 w-6 h-6" />
                    </div>
                    <p className="text-justify text-gray-900 font-light px-2">
                      This catering is organized by{' '}
                      <span className="font-bold">
                        {organizationData.organization?.name}
                      </span>
                      . If you&apos;d like to discuss your needs with one of our
                      booking specialists directly, you can send an email to:{' '}
                      {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
                      <a
                        className="underline font-bold"
                        href={`mailto:${
                          organizationData.organization
                            ?.cateringEmailCustomer ||
                          'sales@bestfoodtrucks.com'
                        }`}
                      >
                        {organizationData.organization?.cateringEmailCustomer ||
                          'sales@bestfoodtrucks.com'}
                      </a>
                    </p>
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      </PageLayout>
    </div>
  )
}

const CateringRequestFormPageWithApollo = withApollo({ ssr: true })(
  withOrganizationTheme(CateringRequestFormPage) as NextPage
)
export default CateringRequestFormPageWithApollo
